import { useEffect, useRef, useState } from 'react';

const domains = [
  { label: 'Hairdresser', sub: '美容師歴21年+', icon: 'ri-scissors-line', angle: 0 },
  { label: 'Marketer', sub: 'EC累計20億', icon: 'ri-line-chart-line', angle: 60 },
  { label: 'EC Specialist', sub: '900冠以上', icon: 'ri-store-line', angle: 120 },
  { label: 'AI Consultant', sub: 'バックオフィス自動化', icon: 'ri-robot-line', angle: 180 },
  { label: 'Grants Consultant', sub: '国策活用', icon: 'ri-government-line', angle: 240 },
  { label: 'Designer', sub: 'ブランド設計', icon: 'ri-palette-line', angle: 300 },
];

export default function InfographicSection() {
  const sectionRef = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);
  const [hovered, setHovered] = useState<number | null>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  const radius = 160;
  const cx = 220;
  const cy = 220;

  return (
    <section id="cta" className="relative bg-[#fafaf8] py-24 md:py-36 overflow-hidden" ref={sectionRef}>
      {/* Subtle background */}
      <div className="absolute inset-0">
        <img
          src="https://public.readdy.ai/ai/img_res/5c514fbff2ab6e155088bab6f6bb516c.jpg"
          alt=""
          className="w-full h-full object-cover opacity-[0.08]"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#fafaf8] via-[#fafaf8]/80 to-[#fafaf8]" />
      </div>

      <div className="relative max-w-7xl mx-auto px-6 md:px-10">
        {/* Header */}
        <div
          className={`text-center mb-16 transition-all duration-1000 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
        >
          <div className="flex items-center justify-center gap-4 mb-6">
            <div className="w-8 h-px bg-[#b4a078]" />
            <span className="text-[#b4a078] text-[10px] tracking-[0.4em] uppercase">Infographic</span>
            <div className="w-8 h-px bg-[#b4a078]" />
          </div>
          <h2
            className="text-[#1a1a1a] font-light leading-tight mb-4"
            style={{ fontFamily: "'Noto Serif JP', serif", fontSize: 'clamp(1.6rem, 3.5vw, 3rem)' }}
          >
            7つの専門領域が、<br />
            <span className="text-[#b4a078]">あなたを中心に有機的に繋がる。</span>
          </h2>
          <p className="text-[#1a1a1a]/40 text-sm">各分野のトップランナーが連携する唯一無二の体制</p>
        </div>

        {/* Infographic + CTA layout */}
        <div className="flex flex-col lg:flex-row items-center gap-16 lg:gap-24">
          {/* Infographic */}
          <div
            className={`flex-shrink-0 transition-all duration-1200 ${visible ? 'opacity-100 scale-100' : 'opacity-0 scale-90'}`}
            style={{ transitionDelay: '300ms' }}
          >
            <svg
              viewBox="0 0 440 440"
              className="w-full max-w-[440px]"
              style={{ filter: 'drop-shadow(0 4px 24px rgba(180,160,120,0.12))' }}
            >
              {/* Outer ring */}
              <circle cx={cx} cy={cy} r={radius + 40} fill="none" stroke="rgba(180,160,120,0.12)" strokeWidth="1" />
              <circle cx={cx} cy={cy} r={radius} fill="none" stroke="rgba(180,160,120,0.2)" strokeWidth="1" strokeDasharray="4 8" />
              <circle cx={cx} cy={cy} r={radius - 50} fill="none" stroke="rgba(26,26,26,0.05)" strokeWidth="1" />

              {/* Connection lines */}
              {domains.map((d, i) => {
                const rad = (d.angle * Math.PI) / 180;
                const x = cx + radius * Math.cos(rad);
                const y = cy + radius * Math.sin(rad);
                return (
                  <line
                    key={i}
                    x1={cx} y1={cy} x2={x} y2={y}
                    stroke={hovered === i ? 'rgba(180,160,120,0.7)' : 'rgba(180,160,120,0.25)'}
                    strokeWidth={hovered === i ? '1.5' : '1'}
                    strokeDasharray={hovered === i ? 'none' : '3 6'}
                    style={{ transition: 'all 0.3s' }}
                  />
                );
              })}

              {/* Domain nodes */}
              {domains.map((d, i) => {
                const rad = (d.angle * Math.PI) / 180;
                const x = cx + radius * Math.cos(rad);
                const y = cy + radius * Math.sin(rad);
                const isHovered = hovered === i;
                return (
                  <g
                    key={i}
                    style={{ cursor: 'pointer', transition: 'all 0.3s' }}
                    onMouseEnter={() => setHovered(i)}
                    onMouseLeave={() => setHovered(null)}
                  >
                    <circle
                      cx={x} cy={y} r={isHovered ? 28 : 24}
                      fill={isHovered ? 'rgba(180,160,120,0.12)' : 'rgba(250,250,248,0.95)'}
                      stroke={isHovered ? 'rgba(180,160,120,0.7)' : 'rgba(180,160,120,0.35)'}
                      strokeWidth="1"
                      style={{ transition: 'all 0.3s' }}
                    />
                    <text
                      x={x} y={y + 4}
                      textAnchor="middle"
                      fontSize="12"
                      fill={isHovered ? '#b4a078' : 'rgba(26,26,26,0.4)'}
                      fontFamily="remixicon"
                      style={{ transition: 'all 0.3s' }}
                    >
                      ●
                    </text>
                    {/* Label */}
                    <text
                      x={x + (Math.cos(rad) > 0.1 ? 36 : Math.cos(rad) < -0.1 ? -36 : 0)}
                      y={y + (Math.sin(rad) > 0.1 ? 36 : Math.sin(rad) < -0.1 ? -36 : 0) + 4}
                      textAnchor={Math.cos(rad) > 0.1 ? 'start' : Math.cos(rad) < -0.1 ? 'end' : 'middle'}
                      fontSize="9"
                      fill={isHovered ? '#b4a078' : 'rgba(26,26,26,0.45)'}
                      fontFamily="sans-serif"
                      letterSpacing="1"
                      style={{ transition: 'all 0.3s' }}
                    >
                      {d.label}
                    </text>
                  </g>
                );
              })}

              {/* Center node */}
              <circle cx={cx} cy={cy} r={36} fill="rgba(180,160,120,0.08)" stroke="rgba(180,160,120,0.35)" strokeWidth="1" />
              <circle cx={cx} cy={cy} r={28} fill="rgba(180,160,120,0.12)" stroke="rgba(180,160,120,0.5)" strokeWidth="1" />
              <text x={cx} y={cy + 4} textAnchor="middle" fontSize="7" fill="rgba(26,26,26,0.35)" fontFamily="sans-serif">
                YOUR BUSINESS
              </text>
            </svg>

            {/* Domain list below infographic on mobile */}
            <div className="grid grid-cols-2 gap-3 mt-6 lg:hidden">
              {domains.map((d, i) => (
                <div key={i} className="flex items-center gap-2 text-xs text-[#1a1a1a]/45">
                  <i className={`${d.icon} text-[#b4a078] text-sm`} />
                  <span>{d.label}</span>
                </div>
              ))}
            </div>
          </div>

          {/* CTA */}
          <div
            className={`flex-1 transition-all duration-1000 ${visible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-10'}`}
            style={{ transitionDelay: '500ms' }}
          >
            <div className="border border-[#1a1a1a]/8 rounded-3xl p-8 md:p-12 bg-white">
              <p className="text-[#b4a078] text-[10px] tracking-[0.4em] uppercase mb-6">Free Consultation</p>
              <h3
                className="text-[#1a1a1a] font-light leading-tight mb-6"
                style={{ fontFamily: "'Noto Serif JP', serif", fontSize: 'clamp(1.4rem, 3vw, 2.4rem)' }}
              >
                最強チームを、<br />
                あなたのものに。
              </h3>
              <p className="text-[#1a1a1a]/45 text-sm leading-relaxed mb-10">
                まずは無料相談から。あなたのビジネスの現状と目標をお聞かせください。
                最適なチーム編成と、具体的な並走プランをご提案します。
              </p>

              {/* Form */}
              <form
                data-readdy-form
                id="tevanashi-consultation-form"
                action="https://readdy.ai/api/form/d7e7r5b5bqmofchk7b00"
                method="POST"
                className="space-y-4"
                onSubmit={async (e) => {
                  e.preventDefault();
                  const form = e.currentTarget;
                  const data = new URLSearchParams(new FormData(form) as unknown as Record<string, string>);
                  try {
                    const res = await fetch('https://readdy.ai/api/form/d7e7r5b5bqmofchk7b00', {
                      method: 'POST',
                      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
                      body: data.toString(),
                    });
                    if (res.ok) {
                      const successEl = document.getElementById('form-success');
                      if (successEl) successEl.classList.remove('hidden');
                      form.reset();
                    }
                  } catch {
                    // silent
                  }
                }}
              >
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-[#1a1a1a]/40 text-[10px] tracking-widest uppercase mb-2">お名前 *</label>
                    <input
                      type="text"
                      name="name"
                      required
                      placeholder="山田 太郎"
                      className="w-full bg-[#fafaf8] border border-[#1a1a1a]/10 rounded-xl px-4 py-3 text-[#1a1a1a] text-sm placeholder-[#1a1a1a]/25 focus:outline-none focus:border-[#b4a078]/60 transition-colors"
                    />
                  </div>
                  <div>
                    <label className="block text-[#1a1a1a]/40 text-[10px] tracking-widest uppercase mb-2">メールアドレス *</label>
                    <input
                      type="email"
                      name="email"
                      required
                      placeholder="your@email.com"
                      className="w-full bg-[#fafaf8] border border-[#1a1a1a]/10 rounded-xl px-4 py-3 text-[#1a1a1a] text-sm placeholder-[#1a1a1a]/25 focus:outline-none focus:border-[#b4a078]/60 transition-colors"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-[#1a1a1a]/40 text-[10px] tracking-widest uppercase mb-2">事業内容・ご相談内容</label>
                  <textarea
                    name="message"
                    rows={4}
                    maxLength={500}
                    placeholder="現在の事業状況や、お悩みをお聞かせください（500文字以内）"
                    className="w-full bg-[#fafaf8] border border-[#1a1a1a]/10 rounded-xl px-4 py-3 text-[#1a1a1a] text-sm placeholder-[#1a1a1a]/25 focus:outline-none focus:border-[#b4a078]/60 transition-colors resize-none"
                  />
                </div>
                <button
                  type="submit"
                  className="w-full bg-[#b4a078] hover:bg-[#a08e68] text-white text-xs tracking-[0.2em] py-4 rounded-xl transition-all duration-300 cursor-pointer whitespace-nowrap font-medium"
                >
                  無料相談を申し込む
                </button>
                <p className="text-[#1a1a1a]/25 text-[10px] text-center">
                  ご入力いただいた情報は、相談対応のみに使用します。
                </p>
              </form>

              {/* Success message */}
              <div id="form-success" className="hidden mt-4 p-4 bg-[#b4a078]/10 border border-[#b4a078]/30 rounded-xl text-center">
                <p className="text-[#b4a078] text-sm">お申し込みありがとうございます。担当者よりご連絡いたします。</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
